/**
 * O que faz: Define os contratos compactos usados pelo carregamento otimizado do dashboard.
 * Onde usa: Serviços, hooks, gráficos, modais de detalhe e relatório do dashboard.
 * Conexões: date-fns, tipos legados de DashboardMetrics e RPCs do Supabase.
 * Tabelas: Representa leituras agregadas de leads, agendamentos, vendas_realizadas e chat_mensagens.
 * Data: 30/08/2026.
 */

import { format, isValid } from 'date-fns';
import type { DashboardMetrics } from '@/hooks/dashboard/types';

export interface PeriodoDashboard {
  inicio: string | null;
  fim: string | null;
}

export interface ContextoDashboard {
  usuarioId: string;
  clinicaId: string;
  modo: 'clinica' | 'admin';
  periodo: PeriodoDashboard;
}

export type FiltroOrigemDashboard = 'todos' | 'anuncios' | 'links_rastreados' | 'meta' | 'google';

export type FiltroContatosDashboard =
  | { tipo: 'todos' | 'anuncios' | 'links_exclusivos' }
  | { tipo: 'anuncio' | 'link'; chave: string }
  // Conjunto/campanha do Meta: chave normalizada igual à do ranking (minúsculas, espaços simples).
  | { tipo: 'conjunto' | 'campanha'; chave: string };

export interface PontoDashboard {
  inicio: string;
  fim: string;
  todos: number;
  anuncios: number;
  links_exclusivos: number;
  links_rastreados: number;
  outros: number;
  meta: number;
  google: number;
}

export interface SemanaDashboard {
  dia: number;
  todos: number;
  anuncios: number;
}

export interface AnuncioDashboard {
  chave: string;
  nome: string;
  apelido: string | null;
  leads: number;
  conversoes: number;
  taxa: number;
}

export interface PaginaAnunciosDashboard {
  itens: AnuncioDashboard[];
  totalItens: number;
  totalLeads: number;
  totalConversoes: number;
  pagina: number;
  tamanho: number;
}

// Agrupamentos do card de anúncios. Conjunto e campanha só existem com o Meta Ads conectado.
export type DimensaoRankingAnuncios = 'anuncio' | 'conjunto' | 'campanha';

export interface RankingOrigensDashboard {
  dimensao: DimensaoRankingAnuncios;
  metaConectado: boolean;
  itens: AnuncioDashboard[];
  totalItens: number;
  totalLeads: number;
  totalConversoes: number;
  // Leads de anúncio sem conjunto/campanha identificados pela Meta (sempre 0 no agrupamento por anúncio).
  semOrigem: number;
}

export interface ContatoDashboard {
  id: string;
  clinica_id: string;
  nome: string | null;
  telefone: string | null;
  created_at: string;
  ad_name: string | null;
  anuncio: string | null;
  link_name: string | null;
  origem_lead: string | null;
}

export interface PaginaContatosDashboard {
  itens: ContatoDashboard[];
  proximoCursor: { created_at: string; id: string } | null;
}

export interface ResumoDashboard {
  periodo: PeriodoDashboard;
  atualizadoEm: string;
  passoDias: number;
  metricas: Pick<DashboardMetrics,
    'totalContatos' | 'leadsAnuncios' | 'leadsComAdName' | 'leadsComLinkName' |
    'leadsLinksPersonalizados' | 'consultasAgendadas' | 'consultasRealizadas' |
    'contatosConvertidos' | 'taxaConversao' | 'conversaoPorOrigem' |
    'faturamentoRealizado' | 'faturamentoConsultas' | 'faturamentoVendas' | 'faturamentoPorForma'>;
  serie: PontoDashboard[];
  semana: SemanaDashboard[];
  anuncios: {
    itens: AnuncioDashboard[];
    totalItens: number;
    totalLeads: number;
    totalConversoes: number;
  };
  links: {
    itens: Array<{ chave: string; nome: string; leads: number; conversoes: number }>;
    totalItens: number;
    totalLeads: number;
    totalConversoes: number;
  };
  categorias: Array<{ category: string; conversions: number }>;
}

export interface AtendimentoDashboard {
  periodo: PeriodoDashboard;
  atualizadoEm: string;
  mensagensEnviadas: number;
  mensagensRecebidas: number;
  totalRespostas: number;
  humano: { quantidade: number; media: number | null };
  ia: { quantidade: number; media: number | null };
  comercial: { quantidade: number; media: number | null };
  media: number | null;
  distribuicao: {
    ate30min: number;
    de30mina1h: number;
    de1ha4h: number;
    acimaDe4h: number;
  };
}

export const normalizarNomeDashboard = (nome: string): string =>
  nome.trim().toLowerCase().replace(/\s+/g, ' ');

export const normalizarOrigemLead = (lead: {
  ad_name?: string | null;
  anuncio?: string | null;
  link_name?: string | null;
}): 'anuncios' | 'links_exclusivos' | 'outros' => {
  // O campo legado anuncio continua válido para não esconder campanhas antigas.
  if (lead.ad_name?.trim() || lead.anuncio?.trim()) return 'anuncios';
  // Um link sem anúncio fica separado para evitar dupla contagem nos cards de origem.
  if (lead.link_name?.trim()) return 'links_exclusivos';
  return 'outros';
};

export const serializarPeriodoDashboard = (
  inicio: Date | null,
  fim: Date | null,
): PeriodoDashboard => {
  // Dois nulos representam o preset Máximo e viram intervalo aberto no SQL.
  if (!inicio && !fim) return { inicio: null, fim: null };
  // Uma data inválida não pode gerar uma consulta parcial ou trocar o dia no fuso do servidor.
  if (!inicio || !fim || !isValid(inicio) || !isValid(fim) || inicio > fim) {
    throw new Error('Selecione um período válido.');
  }
  // A API recebe a data civil escolhida; o banco aplica America/Sao_Paulo no limite semiaberto.
  return { inicio: format(inicio, 'yyyy-MM-dd'), fim: format(fim, 'yyyy-MM-dd') };
};

export const hojeNoCalendarioSaoPaulo = (): Date => {
  // O preset Hoje precisa seguir o fuso do negócio, mesmo quando o navegador está em UTC.
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const lerParte = (tipo: string) => Number(partes.find(parte => parte.type === tipo)?.value);
  return new Date(lerParte('year'), lerParte('month') - 1, lerParte('day'));
};
